// asText.js — PROD-1156. A case-plan value is drawn as TEXT, never as an object.
//
// The follow-up plan (cases.follow_up, the encounter care_plan, finalized_data.followUp) is a
// JSON object md writes field by field: type, timeframe, timingRange {from, to, unit}, visitType,
// notes, referral {specialty, doctorName, notes}, requirements {..., otherText}, ordersPlaced
// {medications: [{name, ...}], labs, ...}, the observation fields and positiveOutcome /
// negativeOutcome. Screens draw single fields of it as text. When one of those fields arrives as
// an object (or the whole plan lands in a text slot), React throws "Objects are not valid as a
// React child (found: object with keys {...})" and the patient gets the crash screen with the
// plan's key names. Every case-plan text slot goes through asText first.
//
// The rule:
//   string            -> itself (byte-identical)
//   number / boolean  -> String(value)
//   null / undefined  -> ''
//   array             -> its scalar items (string / number / boolean), non-empty, joined ', ';
//                        objects and arrays inside are dropped
//   object            -> the first READABLE field that holds a non-empty string or number
//                        (text, label, name, title, description, summary), else ''
//   anything else     -> ''
// ★ NEVER JSON.stringify and NEVER a key name: a 20-key plan object has none of the readable
//   fields, so it reads as '' and the slot falls back to whatever the screen draws for "no value".
// ★ `notes`, `specialty`, `timeframe` are deliberately NOT readable fields: the whole plan carries
//   them, and its notes in an unrelated slot (a visit type, a timeframe) would be wrong text.
//   `name` / `description` are what an order item carries (ordersPlaced lists).
//
// Import-free on purpose: scripts/check-case-plan-astext.mjs loads this file in node.

export const READABLE_KEYS = ['text', 'label', 'name', 'title', 'description', 'summary']

const scalarText = (v) => {
  if (typeof v === 'string') return v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  return ''
}

/** One case-plan value as display text (see the rule above). */
export function asText(value) {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (Array.isArray(value)) return asTextList(value).join(', ')
  if (typeof value === 'object') {
    for (const k of READABLE_KEYS) {
      const v = value[k]
      if ((typeof v === 'string' && v.trim()) || typeof v === 'number') return String(v)
    }
    return ''
  }
  return ''
}

/** The first truthy candidate that reads as non-empty text ('' if none). An object in an earlier slot
 *  falls through to the next real string (PROD-1156 LOW-1); a falsy candidate is skipped as `||` skips it. */
export function asFirst(...values) {
  for (const v of values) {
    if (!v) continue
    const s = asText(v)
    if (s) return s
  }
  return ''
}

/** A case-plan value as a list of display lines: an array's scalar items, or the one asText line. */
export function asTextList(value) {
  if (Array.isArray(value)) return value.map(scalarText).filter((s) => s !== '')
  const one = asText(value)
  return one ? [one] : []
}
